import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { fetchApps } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight, Smartphone } from "lucide-react";

// Sort order (first-wins priority, then alpha by name within each tier):
//   0. com.meridianeventtech.*   — our brand, first-class
//   1. com.latitudedigital.*     — legacy bundles we still manage
//   2. com.unwiredrev.DeviceLink.public — SimpleMDM's own agent app
//   3. everything else           — vendor apps in the MDM catalog
function appSortKey(bundle: string, name: string): [number, string] {
  if (/^com\.meridianeventtech\./.test(bundle)) return [0, name.toLowerCase()];
  if (/^com\.latitudedigital\./.test(bundle)) return [1, name.toLowerCase()];
  if (bundle === "com.unwiredrev.DeviceLink.public") return [2, name.toLowerCase()];
  return [3, name.toLowerCase()];
}

export default function Dashboard() {
  const {
    data: rawApps,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["apps"],
    queryFn: fetchApps,
  });

  const apps = rawApps?.slice().sort((a, b) => {
    const [ag, an] = appSortKey(a.bundle_identifier, a.name);
    const [bg, bn] = appSortKey(b.bundle_identifier, b.name);
    return ag - bg || an.localeCompare(bn);
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">Manage app updates across your supervised devices.</p>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load apps: {error instanceof Error ? error.message : "Unknown error"}
        </div>
      )}

      {isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-56" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-9 w-24" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {apps && apps.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Smartphone className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-lg font-medium">No apps found</p>
            <p className="text-sm text-muted-foreground">
              Apps will appear here once they are added to SimpleMDM.
            </p>
          </CardContent>
        </Card>
      )}

      {apps && apps.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {apps.map((app) => (
            <Card key={app.id} className="transition-shadow hover:shadow-md">
              <CardHeader>
                <CardTitle className="text-lg">{app.name}</CardTitle>
                <CardDescription className="font-mono text-xs">
                  {app.bundle_identifier}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to={`/apps/${app.id}`}>
                  <Button variant="outline" className="gap-2">
                    Manage
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
